import {
  BreadcrumbHeading,
  EditIcon,
  EllipsisMenu,
  Plus,
  PrimaryButton,
  SearchField,
  SnackbarStatus,
  Spinner,
  Table,
  Typography,
  useSnackbar,
} from '@weareopenr/component-library';
import styles from './Roles.module.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import { IRole } from 'src/types';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { IOption } from '@weareopenr/component-library/build/components/Dropdown/Dropdown';
import { useRoles } from 'src/hooks/useRoles/useRoles';
import { usePermissionCatalogue } from 'src/hooks/usePermissionCatalogue/usePermissionCatalogue';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { usePermissions } from 'src/hooks';
import RoleModal from 'src/components/RoleModal/RoleModal';
import SummaryLines from 'src/components/RoleModal/SummaryLines';
import { deriveSummary, ISummaryLine } from 'src/components/RoleModal/summary';

interface IRowSummary {
  lines: ISummaryLine[];
  unrecognised: string[];
}

export const Roles = () => {
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [openRoleModal, setOpenRoleModal] = useState<boolean>(false);
  const [editRole, setEditRole] = useState<IRole | null>(null);
  const { canCreateRoles, canEditRoles } = usePermissions();
  const { roles, isLoading } = useRoles();
  const {
    sections,
    isLoading: isCatalogueLoading,
    error: catalogueError,
  } = usePermissionCatalogue();
  const { showSnackbar } = useSnackbar();
  const { captureException } = useUserTracking();

  // Notify once per failed request; the guard keeps re-renders and unstable
  // callback identities from repeating the snackbar for the same error.
  const notifiedError = useRef<Error | null>(null);
  useEffect(() => {
    if (!catalogueError || notifiedError.current === catalogueError) return;
    notifiedError.current = catalogueError;
    showSnackbar({
      message: catalogueError.message,
      status: SnackbarStatus.ERROR,
    });
    captureException(catalogueError);
  }, [catalogueError, showSnackbar, captureException]);

  const rowSummaries = useMemo(
    () =>
      new Map<string, IRowSummary>(
        (roles ?? []).map((role) => {
          const { lines, unrecognised } = deriveSummary(sections, role.permissions ?? []);
          return [role.slug, { lines, unrecognised }];
        }),
      ),
    [roles, sections],
  );

  const filteredRoles = useMemo(() => {
    if (!searchFilter) {
      return roles;
    }
    return roles?.filter((role) => role.name.toLowerCase().includes(searchFilter.toLowerCase()));
  }, [roles, searchFilter]);

  const columns = useMemo(() => {
    const renderPermissions = (info: CellContext<IRole, string[]>) => {
      const role = info.row.original;
      if (!role.permissions || role.permissions.length === 0) {
        return 'Base permissions only';
      }
      const { lines, unrecognised } = rowSummaries.get(role.slug) ?? {
        lines: [],
        unrecognised: [],
      };

      return (
        <SummaryLines lines={lines}>
          {unrecognised.map((slug) => (
            <li key={slug}>
              <Typography size="small">{slug}</Typography>
            </li>
          ))}
        </SummaryLines>
      );
    };

    const renderRoleDescription = (info: CellContext<IRole, string>) => (
      <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
        {info.getValue() || 'No description'}
      </div>
    );

    const renderRoleActions = (info: CellContext<IRole, unknown>) => {
      const role = info.row.original;
      if (role.type !== 'OrganizationRole') {
        return null;
      }

      const options: IOption[] = [
        {
          id: 'edit',
          name: 'Edit role',
          onOptionClick: () => setEditRole(role),
          icon: EditIcon,
        },
      ];

      return (
        <div className={styles['actions-container']}>
          <EllipsisMenu options={options} variant="ghost" hoverStyle="high-contrast" />
        </div>
      );
    };

    const columnHelper = createColumnHelper<IRole>();

    return [
      columnHelper.accessor('name', {
        header: 'Name',
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        cell: renderRoleDescription,
      }),
      columnHelper.accessor('permissions', {
        header: 'Permissions',
        cell: renderPermissions,
      }),
      ...(canEditRoles
        ? [
            columnHelper.display({
              id: 'actions',
              cell: renderRoleActions,
              size: 65,
            }),
          ]
        : []),
    ];
  }, [canEditRoles, rowSummaries]);

  if (isLoading) {
    return (
      <div className={styles.spinner}>
        <Spinner size="large" />
      </div>
    );
  }

  const isRoleModalOpen = openRoleModal || editRole !== null;
  const closeRoleModal = () => {
    setOpenRoleModal(false);
    setEditRole(null);
  };

  return (
    <div className={styles.container}>
      {(canCreateRoles || canEditRoles) && isRoleModalOpen && (
        <RoleModal
          open={isRoleModalOpen}
          closeModal={closeRoleModal}
          roles={roles}
          role={editRole ?? undefined}
        />
      )}
      <BreadcrumbHeading
        breadcrumbs={[{ label: 'Roles', path: '/roles' }]}
        primaryButton={
          canCreateRoles ? (
            <PrimaryButton
              onClick={() => setOpenRoleModal(true)}
              icon={Plus}
              aria-label="Create Role"
            >
              Create Role
            </PrimaryButton>
          ) : undefined
        }
      />
      <div className={styles['filter-container']}>
        <SearchField
          placeholder="Filter Roles"
          onChange={(e) => setSearchFilter(e.target.value)}
          isDisabled={false}
        />
      </div>
      <div className={styles['table-container']}>
        <Table
          columns={columns}
          data={filteredRoles || []}
          isFetchingNextPage={isCatalogueLoading}
        />
      </div>
    </div>
  );
};
